import React from 'react';
import React, { useState } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { nip19 } from 'nostr-tools';
import { formatDistanceToNow } from 'date-fns'; // We will fallback if not installed, or build custom distance estimator
import { Heart, Repeat, MessageCircle, Share2, Coins, Bookmark, Sparkles, BadgeCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { NoteContent } from '@/components/NoteContent';
import { ContentWarning } from '@/components/ui/ContentWarning';
import { getContentWarning } from '@/lib/feedFilters';
import { useNostr } from '@/hooks/useNostr';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useAppContext } from '@/hooks/useAppContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBookmarks, useToggleBookmark } from '@/hooks/useBookmarks';
import { TipDialog } from './TipDialog';
import { useZaps } from '@/hooks/useZaps';

interface TweetProps {
  event: NostrEvent;
  isReplyThread?: boolean;
  onImageClick?: (url: string) => void;
}

export function Tweet({ event, isReplyThread = false, onImageClick }: TweetProps) {
  const { user } = useCurrentUser();
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { nostr } = useNostr();
  const { mutate: publish } = useNostrPublish();
  const queryClient = useQueryClient();

  const npub = nip19.npubEncode(event.pubkey);
  const noteId = nip19.noteEncode(event.id);

  const { totalSats } = useZaps(event);

  const [isTipOpen, setIsTipOpen] = useState(false);

  // Time formatter helper
  const formattedTime = (() => {
    try {
      const date = new Date(event.created_at * 1000);
      const diffMs = Date.now() - date.getTime();
      const diffMin = Math.round(diffMs / 60000);
      const diffHrs = Math.round(diffMin / 600);
      
      if (diffMin < 1) return 'now';
      if (diffMin < 60) return `${diffMin}m`;
      if (diffHrs < 24) {
        const hrs = Math.floor(diffMin / 60);
        return `${hrs}h`;
      }
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  })();

  // Nostr Interactions & Engagement Queries (Likes / Reposts / Replies)
  const { data: reactions = [] } = useQuery({
    queryKey: ['reactions', event.id],
    queryFn: async () => {
      return nostr.query([{ kinds: [7], '#e': [event.id] }]);
    },
    enabled: !!nostr,
  });

  const { data: reposts = [] } = useQuery({
    queryKey: ['reposts', event.id],
    queryFn: async () => {
      return nostr.query([{ kinds: [6], '#e': [event.id] }]);
    },
    enabled: !!nostr,
  });

  const { data: replies = [] } = useQuery({
    queryKey: ['repliesCount', event.id],
    queryFn: async () => {
      return nostr.query([{ kinds: [1], '#e': [event.id] }]);
    },
    enabled: !!nostr,
  });

  const { data: bookmarkedData } = useBookmarks();
  const toggleBookmark = useToggleBookmark();

  // Check if liked, reposted or bookmarked
  const likedByMe = user ? reactions.some(r => r.pubkey === user.pubkey) : false;
  const repostedByMe = user ? reposts.some(r => r.pubkey === user.pubkey) : false;
  const isBookmarked = bookmarkedData?.bookmarkedIds?.has(event.id) ?? false;

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      toast({ title: 'Sign-in Required', description: 'Log in to like this post!' });
      return;
    }
    if (likedByMe) return;

    publish({
      kind: 7,
      content: '❤',
      tags: [
        ['e', event.id, '', 'root'],
        ['p', event.pubkey]
      ]
    }, {
      onSuccess: () => {
        toast({ title: 'Liked', description: 'Post liked successfully!' });
        queryClient.invalidateQueries({ queryKey: ['reactions', event.id] });
      }
    });
  };

  const handleRepost = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      toast({ title: 'Sign-in Required', description: 'Log in to repost!' });
      return;
    }
    if (repostedByMe) return;

    publish({
      kind: 6,
      content: JSON.stringify(event),
      tags: [
        ['e', event.id, '', 'mention'],
        ['p', event.pubkey]
      ]
    }, {
      onSuccess: () => {
        toast({ title: 'Reposted', description: 'Post reposted successfully!' });
        queryClient.invalidateQueries({ queryKey: ['reposts', event.id] });
      }
    });
  };

  const handleZap = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsTipOpen(true);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/${noteId}`);
    toast({ title: 'Copied Link', description: 'Post link copied to clipboard!' });
  };

  const handleBookmark = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      toast({ title: 'Sign-in Required', description: 'Log in to bookmark notes!' });
      return;
    }
    toggleBookmark.mutate(event.id, {
      onSuccess: (res) => {
        if (res.added) {
          toast({ title: 'Added to Bookmarks 🔖', description: 'Saved successfully to kind-10003 bookmarks!' });
        } else {
          toast({ title: 'Removed from Bookmarks', description: 'Post un-saved successfully.' });
        }
      }
    });
  };

  const replyToId = event.tags.find(t => t[0] === 'e')?.[1];

  return (
    <div 
      onClick={() => navigate(`/${noteId}`)}
      className="p-3 border-b border-border hover:bg-secondary/15 transition-colors cursor-pointer select-none flex flex-col gap-1 w-full"
    >
      {/* Repost Header indicator */}
      {event.kind === 6 && (
        <div className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground ml-9 mb-1">
          <Repeat className="size-3.5" />
          <span>Reposted</span>
        </div>
      )}

      {/* Main Tweet Layout */}
      <div className="flex gap-3 w-full">
        {/* Left Side: Avatar & thread connector line */}
        <div className="flex flex-col items-center shrink-0">
          <img 
            src={metadata?.picture || `https://robohash.org/${event.pubkey}.png?set=set4`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/${npub}`);
            }}
            alt="Author Profile picture" 
            className="size-10 rounded-full object-cover bg-neutral-200 border border-border/80 hover:opacity-90 active:scale-95 transition-all"
          />
          {isReplyThread && (
            <div className="w-0.5 grow bg-border my-1 rounded-full min-h-6" />
          )}
        </div>

        {/* Right Side: Account titles, text content, media cards, and action bar */}
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          
          {/* Header Row: Username, handle, created time */}
          <div className="flex items-center justify-between text-[15px] leading-snug">
            <div className="flex items-center gap-1.5 truncate">
              <span 
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/${npub}`);
                }}
                className="font-extrabold hover:underline truncate text-foreground cursor-pointer flex items-center gap-1"
              >
                {metadata?.display_name || metadata?.name || 'Anonymous'}
                {metadata?.nip05 && (
                  <BadgeCheck className="size-3.5 text-sky-500 fill-sky-500 shrink-0" />
                )}
              </span>
              <span className="text-muted-foreground truncate font-normal text-sm">
                @{npub.slice(0, 8)}...{npub.slice(-4)}
              </span>
              <span className="text-muted-foreground text-sm">·</span>
              <span className="text-muted-foreground hover:underline text-sm shrink-0">
                {formattedTime}
              </span>
            </div>
          </div>

          {/* Replying to handle indicator */}
          {replyToId && (
            <div className="text-xs text-muted-foreground">
              Replying to <span className="text-sky-500 hover:underline">@{replyToId.slice(0, 10)}...</span>
            </div>
          )}

          {/* Expanded Rich Text Content */}
          {(() => {
            const cwReason = event.tags.find(([name]) => name === 'content-warning')?.[1];
            const note = <NoteContent event={event} className="mt-1" onImageClick={(url) => onImageClick?.(url)} />;
            
            if (cwReason) {
              const policy = localStorage.getItem('content-warning-policy') || 'blur';
              if (policy === 'hide') return null;
              return (
                <ContentWarning reason={cwReason}>
                  {note}
                </ContentWarning>
              );
            }
            return note;
          })()}

          {/* X Interactive Button Action Row bar */}
          <div className="flex items-center justify-between text-muted-foreground max-w-md mt-3 pr-2 -ml-2 text-xs">
            {/* Reply action */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/${noteId}`);
              }}
              className="group flex items-center gap-1.5 hover:text-sky-500 transition-colors p-2 -m-2 rounded-full cursor-pointer"
            >
              <span className="p-1 rounded-full group-hover:bg-sky-500/15 transition-colors">
                <MessageCircle className="size-4 group-hover:stroke-sky-500" />
              </span>
              <span className="tabular-nums font-normal">{replies.length || ''}</span>
            </button>

            {/* Repost action */}
            <button 
              onClick={handleRepost}
              className={`group flex items-center gap-1.5 hover:text-green-500 transition-colors p-2 -m-2 rounded-full cursor-pointer ${repostedByMe ? 'text-green-500' : ''}`}
            >
              <span className="p-1 rounded-full group-hover:bg-green-500/15 transition-colors">
                <Repeat className={`size-4 group-hover:stroke-green-500 ${repostedByMe ? 'stroke-[2.5px]' : ''}`} />
              </span>
              <span className="tabular-nums font-normal">{reposts.length || ''}</span>
            </button>

            {/* Like action */}
            <button 
              onClick={handleLike}
              className={`group flex items-center gap-1.5 hover:text-pink-600 transition-colors p-2 -m-2 rounded-full cursor-pointer ${likedByMe ? 'text-pink-600' : ''}`}
            >
              <span className="p-1 rounded-full group-hover:bg-pink-600/15 transition-colors">
                <Heart className={`size-4 group-hover:stroke-pink-600 ${likedByMe ? 'fill-pink-600 stroke-pink-600' : ''}`} />
              </span>
              <span className="tabular-nums font-normal">{reactions.length || ''}</span>
            </button>

            {/* Bitcoin Zap / Tip action */}
            <button 
              onClick={handleZap}
              disabled={!metadata?.lud16 && !metadata?.lud06}
              className={`group flex items-center gap-1.5 hover:text-amber-500 transition-colors p-2 -m-2 rounded-full cursor-pointer ${totalSats > 0 ? 'text-amber-500' : ''} ${(!metadata?.lud16 && !metadata?.lud06) ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={(!metadata?.lud16 && !metadata?.lud06) ? 'User has not configured Lightning Tips' : 'Send a tip'}
            >
              <span className="p-1 rounded-full group-hover:bg-amber-500/15 transition-colors">
                <Coins className={`size-4 group-hover:stroke-amber-500 ${totalSats > 0 ? 'fill-amber-500 stroke-amber-500' : ''}`} />
              </span>
              <span className="tabular-nums font-normal">{totalSats > 0 ? `${totalSats} sats` : 'Tip'}</span>
            </button>

            {/* Bookmark button */}
            <button 
              onClick={handleBookmark}
              className={`group flex items-center hover:text-sky-500 transition-colors p-2 -m-2 rounded-full cursor-pointer ${isBookmarked ? 'text-sky-500' : ''}`}
            >
              <span className="p-1 rounded-full group-hover:bg-sky-500/15 transition-colors">
                <Bookmark className={`size-4 group-hover:stroke-sky-500 ${isBookmarked ? 'fill-sky-500 stroke-sky-500' : ''}`} />
              </span>
            </button>

            {/* Share link action */}
            <button 
              onClick={handleShare}
              className="group flex items-center hover:text-sky-500 transition-colors p-2 -m-2 rounded-full cursor-pointer"
            >
              <span className="p-1 rounded-full group-hover:bg-sky-500/15 transition-colors">
                <Share2 className="size-4 group-hover:stroke-sky-500" />
              </span>
            </button>
          </div>

        </div>
      </div>

      {/* Tip presets invoice popup dialogue context */}
      <TipDialog event={event} isOpen={isTipOpen} onOpenChange={setIsTipOpen} />

    </div>
  );
}
